import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Helper function to validate URLs
const isValidUrl = (url) => {
  try {
    new URL(url);
    return url.startsWith("http://") || url.startsWith("https://");
  } catch {
    return false;
  }
};

// Parameter definitions
const parameterDefinitions = [
  { key: "line", label: "Line" },
  { key: "module", label: "Module" },
  { key: "ivParameters", label: "IV Parameters" },
  { key: "zip_url", label: "Zip URL" },
];

// Default module object
const defaultModule = {
  line: "N/A",
  module: "N/A",
  ivParameters: null,
  zip_url: "N/A",
};

const RFIDReadabilityTable = ({ data, startDate, endDate, shifts, subForm }) => {
  // Debug: Log received props
  console.log("RFIDReadabilityTable props:", { data, startDate, endDate, shifts, subForm });

  // Define static table headers
  const staticHeaders = [
    { id: "parameter", label: "Parameter" },
  ];

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();
  console.log("Effective Shifts:", effectiveShifts);

  // Build data map for easier lookup
  const dataMap = useMemo(() => {
    const map = {};
    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      const key = `${date}_${shift}`;
      const modules = item.form_data?.modules || {};
      // Include zip_url in each module
      const updatedModules = {};
      for (let i = 1; i <= 5; i++) {
        updatedModules[`module${i}`] = {
          ...defaultModule,
          ...(modules[`module${i}`] || {}),
          zip_url: item.zip_url && item.zip_url.trim() !== "" && isValidUrl(item.zip_url) ? item.zip_url : "N/A",
        };
      }
      map[key] = updatedModules;
      console.log(`DataMap entry for ${key}:`, updatedModules);
    });
    return map;
  }, [data]);

  // Define maximum number of modules (fixed at 5 based on JSON structure)
  const maxModules = 5;

  // Create dynamic headers
  const dynamicHeaders = useMemo(() => {
    const headers = [];
    dates.forEach((date) => {
      if (!startDate || !endDate || (date >= formatDate(startDate) && date <= formatDate(endDate))) {
        effectiveShifts.forEach((shift) => {
          const key = `${date}_${shift}`;
          for (let i = 0; i < maxModules; i++) {
            headers.push({
              id: `${key}_module_${i + 1}`,
              label: `Module ${i + 1}`,
              dateShift: `${date} ${shift}`,
              moduleIndex: i,
            });
          }
        });
      }
    });
    return headers;
  }, [dates, effectiveShifts, maxModules, startDate, endDate]);

  // Group headers by date-shift for top-level headers
  const topLevelHeaders = useMemo(() => {
    const headers = [];
    const seen = new Set();
    dynamicHeaders.forEach((header) => {
      if (!seen.has(header.dateShift)) {
        seen.add(header.dateShift);
        const moduleCount = dynamicHeaders.filter((h) => h.dateShift === header.dateShift).length;
        headers.push({
          id: header.dateShift,
          label: header.dateShift,
          colSpan: moduleCount,
        });
      }
    });
    return headers;
  }, [dynamicHeaders]);

  // Calculate column widths
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: estimateTextWidth("Parameter"),
      value: 100, // Default width for value columns
    };

    // Width for parameter column
    parameterDefinitions.forEach((param) => {
      widths.parameter = Math.min(
        Math.max(widths.parameter, estimateTextWidth(param.label)),
        8 * window.innerWidth / 100
      );
    });

    // Width for value columns based on data
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const modules = dataMap[key] || {};
        for (let i = 1; i <= maxModules; i++) {
          const module = modules[`module${i}`] || defaultModule;
          const fields = [
            module.line,
            module.module,
            module.ivParameters && module.ivParameters.length > 0 ? module.ivParameters.length.toString() : "N/A",
            module.zip_url === "N/A" ? "N/A" : "LINK",
          ];
          fields.forEach((value) => {
            widths.value = Math.min(
              Math.max(
                widths.value,
                estimateTextWidth(`Module ${i}`),
                estimateTextWidth(value)
              ),
              8 * window.innerWidth / 100
            );
          });
        }
      });
    });

    dynamicHeaders.forEach((header) => {
      widths[header.id] = widths.value;
    });

    topLevelHeaders.forEach((header) => {
      widths[header.id] = dynamicHeaders
        .filter((h) => h.dateShift === header.id)
        .reduce((sum) => sum + widths.value, 0);
    });

    return widths;
  }, [dataMap, dynamicHeaders, topLevelHeaders, maxModules, dates, effectiveShifts]);

  // Calculate left position for sticky column
  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    const rows = parameterDefinitions.map((param) => ({
      parameter: param.label,
      data: {},
    }));

    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        const key = `${date}_${shift}`;
        const modules = dataMap[key] || {};
        for (let i = 1; i <= maxModules; i++) {
          const moduleKey = `${key}_module_${i}`;
          const module = modules[`module${i}`] || defaultModule;
          rows.find((r) => r.parameter === "Line").data[moduleKey] = module.line;
          rows.find((r) => r.parameter === "Module").data[moduleKey] = module.module;
          rows.find((r) => r.parameter === "IV Parameters").data[moduleKey] =
            module.ivParameters && module.ivParameters.length > 0 ? module.ivParameters.length.toString() : "N/A";
          rows.find((r) => r.parameter === "Zip URL").data[moduleKey] = module.zip_url;
        }
      });
    });

    return rows;
  }, [dataMap, maxModules, dates, effectiveShifts]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    // Row 1: Date + Shift (merged across 5 modules)
    const row1 = ["Parameter"];
    let colIdx = 1;

    topLevelHeaders.forEach((header) => {
      row1.push(header.label);
      if (header.colSpan > 1) {
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + header.colSpan - 1 } });
      }
      colIdx += header.colSpan;
    });

    // Row 2: Module 1 to Module 5
    const row2 = ["Parameter"];
    dynamicHeaders.forEach((h) => row2.push(h.label));

    // Data rows
    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      dynamicHeaders.forEach((header) => {
        const value = row.data[header.id] || "N/A";

        // Make zip_url clickable in Excel
        if (row.parameter === "Zip URL" && value !== "N/A" && value.startsWith("http")) {
          r.push(`=HYPERLINK("${value}", "DOWNLOAD")`);
        } else {
          r.push(value === "N/A" ? "" : value);
        }
      });
      return r;
    });

    const sheetData = [row1, row2, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Apply merged cells
    if (merges.length > 0) ws["!merges"] = merges;

    // Column widths
    ws["!cols"] = [
      { wch: 28 }, // Parameter
      ...Array(dynamicHeaders.length).fill({ wch: 22 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "RFID Readability");

    const fileName = `RFID_Readability_Test_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        RFID Readability and Data Verification
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dynamicHeaders.length === 0}
      >
        Download as Excel
      </Button>
      
      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              <StickyTableCell
                rowSpan={2}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {topLevelHeaders.map((header) => (
                <CustomTableCell
                  key={header.id}
                  colSpan={header.colSpan}
                  columnWidth={columnWidths[header.id]}
                >
                  {header.label}
                </CustomTableCell>
              ))}
            </TableRow>
            <TableRow>
              {dynamicHeaders.map((header) => (
                <CustomTableCell
                  key={header.id}
                  columnWidth={columnWidths[header.id]}
                >
                  {header.label}
                </CustomTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={staticHeaders.length + dynamicHeaders.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>
                  {dynamicHeaders.map((header) => (
                    <BodyTableCell
                      key={`${header.id}_${index}`}
                      columnWidth={columnWidths[header.id]}
                    >
                      {row.parameter === "Zip URL" && row.data[header.id] !== "N/A" ? (
                        <a
                          href={row.data[header.id]}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#1976d2", textDecoration: "underline" }}
                        >
                          LINK
                        </a>
                      ) : (
                        row.data[header.id]
                      )}
                    </BodyTableCell>
                  ))}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default RFIDReadabilityTable;